import { sessionEpoch, advanceSessionEpoch, notifySessionReset, pendingOperationCount } from './session-lifecycle.ts';
import type { AuthTokens, User } from '@fin/shared';
const BASE = '/api/v1';
export class ApiError extends Error { constructor(public status: number, message: string) { super(message); } }
export class NetworkError extends Error { constructor() { super('Нет связи с сервером'); } }
let online = typeof navigator === 'undefined' ? true : navigator.onLine;
const netListeners = new Set<() => void>();
function setOnline(next: boolean) { if (next !== online) { online = next; netListeners.forEach((l) => l()); } }
export const netStore = { subscribe(l: () => void) { netListeners.add(l); return () => { netListeners.delete(l); }; }, get: () => online };
if (typeof window !== 'undefined') window.addEventListener('offline', () => setOnline(false));
async function reach(url: string, init: RequestInit): Promise<Response> {
  let res: Response;
  try { res = await fetch(url, init); } catch (e) { if (init.signal?.aborted) throw e; setOnline(false); throw new NetworkError(); }
  if (res.status === 502 || res.status === 504) { setOnline(false); throw new NetworkError(); }
  setOnline(true); return res;
}
let accessToken: string | null = null, currentUser: User | null = null;
type AuthState = { status: 'loading' | 'in' | 'out'; user: User | null };
let state: AuthState = { status: 'loading', user: null };
const listeners = new Set<() => void>();
function setState(next: AuthState) { state = next; listeners.forEach((l) => l()); }
export const authStore = { subscribe(l: () => void) { listeners.add(l); return () => { listeners.delete(l); }; }, get: () => state };
const USER_KEY = 'fin.user', EMAIL_KEY = 'fin.email', OUT_KEY = 'fin.signedOut';
export { sessionEpoch, onSessionReset } from './session-lifecycle.ts';
function localOut() { try { return localStorage.getItem(OUT_KEY) === '1'; } catch { return false; } }
function markOut(out: boolean) { try { if (out) localStorage.setItem(OUT_KEY, '1'); else localStorage.removeItem(OUT_KEY); } catch { /* storage unavailable */ } }
function resetData() {
  notifySessionReset();
  try {
    for (const key of Object.keys(localStorage)) if (key.startsWith('fin.cache.') || key.startsWith('fin.last.') || key === 'fin.queue.v1') localStorage.removeItem(key);
  } catch { /* memory is still cleared */ }
}
function rememberUser(user: User | null) { try { if (user) localStorage.setItem(USER_KEY, JSON.stringify(user)); else localStorage.removeItem(USER_KEY); } catch {} }
function rememberedUser(): User | null { try { return JSON.parse(localStorage.getItem(USER_KEY) ?? 'null'); } catch { return null; } }
function rememberEmail(email: string) { try { localStorage.setItem(EMAIL_KEY, email); } catch {} }
export function lastEmail(): string { try { return localStorage.getItem(EMAIL_KEY) ?? rememberedUser()?.email ?? ''; } catch { return ''; } }
function accept(t: AuthTokens) {
  if (currentUser && currentUser.id !== t.user.id) resetData();
  accessToken = t.accessToken; currentUser = t.user; rememberUser(t.user); markOut(false);
  setState({ status: 'in', user: t.user });
}
export function signedOut() {
  if (currentUser) rememberEmail(currentUser.email);
  markOut(true); accessToken = null; currentUser = null; rememberUser(null); resetData();
  setState({ status: 'out', user: null });
}
export const hasSession = () => accessToken !== null;
let refreshing: Promise<boolean> | null = null;
export function refreshSession(): Promise<boolean> {
  if (localOut()) { if (state.status !== 'out') signedOut(); return Promise.resolve(false); }
  if (refreshing) return refreshing;
  const started = sessionEpoch();
  refreshing = (async () => {
    try {
      const r = await reach(BASE + '/auth/refresh', { method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json' }, body: '{}' });
      if (started !== sessionEpoch() || localOut()) return false;
      if (r.status === 401 || r.status === 400) { signedOut(); return false; }
      if (!r.ok) throw new NetworkError();
      const data = await r.json();
      if (started !== sessionEpoch() || localOut()) return false;
      accept(data); return true;
    } catch {
      if (started !== sessionEpoch() || localOut()) return false;
      const user = currentUser ?? rememberedUser();
      if (user) { currentUser = user; setState({ status: 'in', user }); }
      else setState({ status: 'out', user: null });
      return false;
    }
  })().finally(() => { refreshing = null; });
  return refreshing;
}
export async function login(email: string, password: string) {
  const started = advanceSessionEpoch();
  // Revoke a cookie left behind by an offline logout before creating the new session.
  if (localOut()) await reach(BASE + '/auth/logout', { method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json' }, body: '{}' });
  const r = await reach(BASE + '/auth/login', { method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ email, password, cookie: true, device: 'web' }) });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new ApiError(r.status, data.error ?? 'Не удалось войти');
  if (started !== sessionEpoch()) return;
  rememberEmail(email); accept(data);
}
export async function logout() {
  if (pendingOperationCount() && !window.confirm('Неотправленные операции будут удалены с устройства. Выйти?')) return;
  signedOut();
  // A failed server call must not undo the persistent local logout marker.
  await fetch(BASE + '/auth/logout', { method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json' }, body: '{}' }).catch(() => {});
}
export function setUser(user: User) { if (currentUser && currentUser.id !== user.id) resetData(); currentUser = user; rememberUser(user); setState({ status: 'in', user }); }
export const getUser = () => currentUser;
if (typeof window !== 'undefined') window.addEventListener('storage', (event) => {
  if (event.key === OUT_KEY && event.newValue === '1') signedOut();
  else if (event.key === USER_KEY && event.newValue && currentUser) {
    try { if (JSON.parse(event.newValue).id !== currentUser.id) signedOut(); } catch { signedOut(); }
  }
});
export async function api<T = unknown>(path: string, opts: { method?: string; body?: unknown; signal?: AbortSignal } = {}): Promise<T> {
  const started = sessionEpoch();
  if (!currentUser || localOut()) throw new ApiError(401, 'Требуется вход');
  const method = opts.method ?? 'GET';
  const send = () => reach(BASE + path, { method, credentials: 'same-origin', signal: opts.signal,
    headers: { ...(opts.body !== undefined ? { 'content-type': 'application/json' } : {}), ...(accessToken ? { authorization: 'Bearer ' + accessToken } : {}) },
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined });
  let res = await send();
  if (started !== sessionEpoch()) throw new ApiError(401, 'Сессия изменена');
  if (res.status === 401 && await refreshSession()) res = await send();
  if (started !== sessionEpoch()) throw new ApiError(401, 'Сессия изменена');
  const text = await res.text(), data = text ? JSON.parse(text) : undefined;
  if (started !== sessionEpoch()) throw new ApiError(401, 'Сессия изменена');
  if (!res.ok) throw new ApiError(res.status, data?.error ?? 'Ошибка ' + res.status);
  return data as T;
}
export const get = <T>(path: string) => api<T>(path);
export const post = <T>(path: string, body?: unknown) => api<T>(path, { method: 'POST', body: body ?? {} });
export const patch = <T>(path: string, body: unknown) => api<T>(path, { method: 'PATCH', body });
export const put = <T>(path: string, body: unknown) => api<T>(path, { method: 'PUT', body });
export const del = <T>(path: string) => api<T>(path, { method: 'DELETE' });
export function qs(params: Record<string, string | number | undefined | null | false>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== '' && v !== false) p.set(k, String(v));
  return p.size ? '?' + p.toString() : '';
}
